import SAVED_TYPE from './saved-from-enum';
import mixpanel from './mixpanelClient';
import jwt_decode from 'jwt-decode'
import { differenceInSeconds, differenceInDays } from 'date-fns';

const hasProductAnalyticsSession = () => {
    try {
        return typeof window !== 'undefined' && Boolean(localStorage.getItem('token'));
    } catch (error) {
        return false;
    }
};

// Safe wrapper for Mixpanel calls to handle CORS errors (especially in Firefox)
const safeMixpanelCall = (fn, ...args) => {
    try {
        // Product events never load Mixpanel for anonymous public visitors.
        if (
            hasProductAnalyticsSession() &&
            mixpanel &&
            typeof mixpanel.has_opted_out_tracking === 'function' &&
            !mixpanel.has_opted_out_tracking()
        ) {
            return fn.apply(mixpanel, args);
        }
    } catch (error) {
        // Suppress CORS errors and network errors - these are expected in some browsers/environments
        const isCorsError = error.message && (
            error.message.includes('CORS') ||
            error.message.includes('Cross-Origin') ||
            error.message.includes('NetworkError') ||
            error.name === 'NetworkError'
        );
        
        // Only log non-CORS errors in development
        if (!isCorsError && process.env.NODE_ENV === 'development') {
            console.warn('Mixpanel call failed:', error);
        }
        // Silently fail for CORS errors - analytics shouldn't break the app
    }
};

// Safe wrappers for common Mixpanel methods
const safeTrack = (...args) => safeMixpanelCall(mixpanel.track, ...args);
const safeIdentify = (...args) => safeMixpanelCall(mixpanel.identify, ...args);
const safePeopleSet = (...args) => {
    if (mixpanel.people && typeof mixpanel.people.set === 'function') {
        return safeMixpanelCall(mixpanel.people.set, ...args);
    }
};
const safePeopleIncrement = (...args) => {
    if (mixpanel.people && typeof mixpanel.people.increment === 'function') {
        return safeMixpanelCall(mixpanel.people.increment, ...args);
    }
};
const safeReset = () => safeMixpanelCall(mixpanel.reset);
const safeGetDistinctId = () => {
    try {
        if (hasProductAnalyticsSession() && mixpanel && typeof mixpanel.get_distinct_id === 'function') {
            return mixpanel.get_distinct_id();
        }
    } catch (error) {
        // Return a fallback ID if Mixpanel fails
        return null;
    }
};
const safeGetProperty = (prop) => {
    try {
        if (hasProductAnalyticsSession() && mixpanel && typeof mixpanel.get_property === 'function') {
            return mixpanel.get_property(prop);
        }
    } catch (error) {
        return null;
    }
};

// Legacy event calls remain in this module while product event names migrate;
// Google Analytics is no longer loaded on the site.
const safeReactGA = () => undefined;

function pageview(path, trackerNames, viewObject) {
    safeTrack('Page View', {
        'path': path,
        'screen_name': trackerNames,
        ...viewObject
    });
    safeReactGA('pageview', path, trackerNames);
}

// Mixpanel-only pageview for in-app routes (/f/..., /solucky) that are
// excluded from Umami/GA. Tracks navigation flow without sending folder
// URLs to third-party marketing analytics.
function app_pageview(path, screenName) {
    safeTrack('Page View', {
        'path': path,
        'screen_name': screenName,
        'in_app': true
    });
}

// Umami custom events (marketing funnel only — anonymous counts, no PII/revenue).
// window.umami is absent on localhost, for admins, and when blocked by privacy tools.
const safeUmamiEvent = (name, data) => {
    try {
        if (window.umami && typeof window.umami.track === 'function') {
            window.umami.track(name, data);
        }
    } catch (error) {
        // Analytics must never break the app
    }
};

function reminder_action(actionObject) {
    safeTrack('Reminder', actionObject);
}

function ai_suggestion(actionObject) {
    safeTrack('AI Suggestion', actionObject);
}

function checkout_started(plan, quantity) {
    safeTrack('Checkout Started', {
        'plan_id': plan?.id,
        'plan_title': plan?.title,
        'plan_type': plan?.type,
        'price': plan?.price,
        'quantity': quantity
    });
    safeUmamiEvent('checkout-started', { plan_type: plan?.type });
}

function purchase_completed(source) {
    safeTrack('Purchase Completed', {
        'source': source || 'checkout'
    });
    safeUmamiEvent('purchase-completed', { source: source || 'checkout' });
}

function published_folder_viewed({ token, folderId, folderTitle, allowCopy, discoverable, messageCount }) {
    safeTrack('Published Folder Viewed', {
        published_token: token,
        folder_id: folderId,
        folder_title: folderTitle,
        allow_copy: Boolean(allowCopy),
        discoverable: Boolean(discoverable),
        visible_message_count: messageCount,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        referrer: typeof document !== 'undefined' ? document.referrer || null : null
    });
}

function published_folder_action({ token, folderId, folderTitle, action, intent, isLoggedIn }) {
    safeTrack('Published Folder Action', {
        published_token: token,
        folder_id: folderId,
        folder_title: folderTitle,
        action,
        intent: intent || null,
        is_logged_in: Boolean(isLoggedIn),
        path: typeof window !== 'undefined' ? window.location.pathname : null
    });
}

function published_folder_signup_conversion({ token, intent, copiedFolderId, copiedFolderTitle }) {
    safeTrack('Published Folder Signup Conversion', {
        published_token: token,
        intent: intent || null,
        copied_folder_id: copiedFolderId || null,
        copied_folder_title: copiedFolderTitle || null
    });
}

function messageview(actionObject, message) {
    let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);

    safeTrack('Message View', {
        ...actionObject,
        ...messageContent
    });
}

function modalview(name) {
    safeTrack('Modal Open', {
        "modal_name": name
    });
    safeReactGA('modalview', 'Modal Open', {
        "modal_name": name
    });
}

function addAnalytics(path, user) {
    safeIdentify(user.id);
    const peopleData = {
        "$email": user.email,
        "$name": user.fullName,
        "$created": user.createdAt,
        "User ID": user.id,
        "Role": user.role,
        "Plan": user.planType
        // "$avatar": user.profilePictureUrl,
        // 'Background Picture': user.backgroundImageUrl
    };
    
    // Add discovery source if available
    if (user.discoverySource) {
        peopleData["Discovery Source"] = user.discoverySource;
    }
    if (user.discoverySourceOther) {
        peopleData["Discovery Source Other"] = user.discoverySourceOther;
    }
    
    safePeopleSet(peopleData);
    safeReactGA('set', { userId: user.id });
}

function addProfilePic(url) {
    safeTrack('Add Profile Picture');
    // console.log('url', url);
    // no need for userId
    safePeopleSet({
        "$avatar": url
    })
}

function addBackgroundPic(url) {
    safeTrack('Add Background Picture');
    safePeopleSet({
        "Background Picture": url
    })
}

function removeProfilePic() {
    safeTrack('Remove Profile Picture');
}

function event(args) {
    safeTrack(args)
    safeReactGA('event', args);
}

function login(payload) {
    // Only process if token exists (successful login)
    if (!payload || !payload.token) {
        return;
    }

    try {
        const { token } = payload;
        const decoded = jwt_decode(token);
        if (decoded) {
            const { _id: userId, role } = decoded;
            safeIdentify(userId);
            safeTrack('Login');
            safeReactGA('event', {
                category: 'User',
                action: 'Login'
            });
            // mixpanel.people.set({
            //     plan: "Personal"
            // })
            // mixpanel.people.set({
            //     "$name": name,
            //     "$email": email
            // });
        }
    } catch (error) {
        // Silently fail if token is invalid - don't break login flow
        console.warn('Failed to decode token for analytics:', error.message);
    }
}

function sign_out() {
    safeTrack('Sign Out');
    safeReset();
    safeReactGA('event', {
        category: 'User',
        action: 'Sign Out'
    });
    // mixpanel.reset()
    // mixpanel.unregister()
}

function delete_account(deletionReasons, additionalFeedback) {
    try {
        const deletionData = {
            deletion_reasons: deletionReasons || [],
            deletion_reasons_count: deletionReasons?.length || 0,
            has_additional_feedback: !!additionalFeedback,
            additional_feedback_length: additionalFeedback?.length || 0
        };

        // Track individual reasons
        if (deletionReasons && deletionReasons.length > 0) {
            deletionReasons.forEach(reason => {
                deletionData[`reason_${reason}`] = true;
            });
        }

        // Include feedback if provided (truncated for privacy/analytics limits)
        if (additionalFeedback) {
            deletionData.additional_feedback_preview = additionalFeedback.substring(0, 200);
        }

        safeTrack('Delete Account', deletionData);
        safeReset();
    } catch (error) {
        // Don't throw - analytics failures shouldn't block user flow
        if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to track account deletion in Mixpanel:', error);
        }
        // Silently fail - user deletion should proceed even if analytics fails
    }
}

async function sign_up(payload) {
    // Only process if token exists (successful signup)
    if (!payload || !payload.token) {
        return;
    }

    try {
        const { token } = payload;
        const decoded = jwt_decode(token);
        if (decoded) {
            const { _id: userId, role } = decoded;
            // Anonymous public visitors can be counted as a signup conversion
            // in Umami without attaching an account or person identifier.
            safeUmamiEvent('sign-up-completed');
            // Account creation is tracked by the server after the transaction commits.
            // 'source': "Pat's affiliate site",
            // To create a user profile, you must call identify()
            safeIdentify(userId);
            safePeopleSet({
                "Plan": "Personal"
            })

            // this one doesn't work, or didn't set it up correctly
            // ReactGA.ga('event', 'conversion', {
            //     'send_to': 'AW-10818851857/VdMYCPDT7I4DEJGo6qYo',
            //     //    'value': 25.0,
            //     //    'currency': 'EUR'
            // });

            safeReactGA('event', {
                category: 'User',
                action: 'Created an Account'
            });

            // ReactPixel.fbq('track', 'Purchase', { currency: 'USD', value: 1.0 });
        }
    } catch (error) {
        // Silently fail if token is invalid - don't break signup flow
        console.warn('Failed to decode token for analytics:', error.message);
    }
}

function sign_up_pro() {
    safeTrack('Pro Signup', {
        "pro": true
    })
    safeReactGA('event', {
        category: "User",
        action: 'Sign Up Pro'
    });
}

const extractMessageInfo = (message, justCreated) => {
    const messageContent = {};
    const origin = message?.metadata?.savedFrom;
    if (['WHATSAPP', 'TELEGRAM', 'SLACK', 'X', 'EMAIL', 'WEB', 'MOBILE', 'EXTENSION', 'MOBILE_EXTENSION', 'MCP', 'UNKNOWN'].includes(origin?.source)) messageContent.save_source = origin.source;
    if (['MANUAL', 'IMPORT', 'AUTO_SYNC', 'MESSAGING', 'GENERATED', 'UNKNOWN'].includes(origin?.method)) messageContent.save_method = origin.method;
    const channel = origin?.channel;
    if (['WHATSAPP', 'TELEGRAM'].includes(channel)) {
        messageContent.channel = channel;
        messageContent.integration = channel.toLowerCase();
    }
    const urlMatches = message.title && message.title.match(/\bhttps?:\/\/\S+/gi);

    if (message.folder?.title === 'Inbox') {
        if (message.taskFolderMessage) {
            messageContent["screen"] = "TasksFolder";
        } else {
            messageContent["screen"] = "Inbox";
        }
    } else if (message.folder?.parent) {
        messageContent["screen"] = "Subfolder";
    } else if (message.folder) {
        messageContent["screen"] = "HomeFolder";
    }
    if (message.title) {
        messageContent["has_title"] = true
        messageContent["title_length"] = message.title.length
    }
    if (message.isCheckbox) {
        messageContent["has_checkbox"] = true;
        messageContent["is_checked"] = message.isChecked;
    }
    if (message.isStarred) {
        messageContent["has_star"] = true;
    }
    if (message.body) {
        messageContent["has_note"] = true
        messageContent["note_length"] = message.body.length
    }
    if (message.image) {
        messageContent["has_image"] = true
        messageContent["image_size"] = message.image.size;
    }
    if (message.file) {
        messageContent["has_file"] = true
        messageContent["file_size"] = message.file.size;
    }
    if (message.file && message.file.type.includes('audio')) {
        messageContent["has_audio"] = true
    }
    if (urlMatches && urlMatches.length > 0) {
        messageContent["has_link"] = true;
        messageContent["total_links"] = urlMatches.length;
    }
    if (message.deletedAt) {
        messageContent["is_deleted"] = true;
        messageContent["delete_to_now_days"] = differenceInDays(new Date(), new Date(message.deletedAt));
    }
    if (!justCreated) {
        if (message.createdAt) {
            messageContent["create_to_now_days"] = differenceInDays(new Date(), new Date(message.createdAt));
            messageContent["create_to_now_seconds"] = differenceInSeconds(new Date(), new Date(message.createdAt));
            messageContent["creation_date"] = message.createdAt;
        }

        if (message.updatedAt) {
            messageContent["update_to_now_days"] = differenceInDays(new Date(), new Date(message.updatedAt));
            messageContent["update_to_now_seconds"] = differenceInSeconds(new Date(), new Date(message.updatedAt));
            messageContent["updated_date"] = message.updatedAt;
        }
    }
    messageContent["msg_id"] = message.id;
    messageContent["platform"] = 'web';
    messageContent["app_platform"] = 'web';
    if (message?.metadata?.savedFrom?.type) {
        messageContent["saved_from_type"] = message.metadata.savedFrom.type;
    }

    return messageContent;
}

const extractFolderInfo = (folder, justCreated) => {
    const folderContent = {};

    if (!justCreated) {
        if (folder.createdAt) {
            folderContent["create_to_now_days"] = differenceInDays(new Date(), new Date(folder.createdAt));
            folderContent["create_to_now_seconds"] = differenceInSeconds(new Date(), new Date(folder.createdAt));
            folderContent["creation_date"] = folder.createdAt;
        }

        if (folder.updatedAt) {
            folderContent["update_to_now_days"] = differenceInDays(new Date(), new Date(folder.updatedAt));
            folderContent["update_to_now_seconds"] = differenceInSeconds(new Date(), new Date(folder.updatedAt));
            folderContent["updated_date"] = folder.updatedAt;
        }
    }
    folderContent["folder_id"] = folder.id;

    return folderContent;
}

const extractMixpanelObject = (message) => {
    if (!message) {
        console.warn('Message object is null in extractMixpanelObject');
        return {};
    }

    const mixpanelObject = {};

    // Check if message.title exists before using match
    const urlMatches = message.title ? message.title.match(/\bhttps?:\/\/\S+/gi) : null;

    // Check if message.folder exists before accessing title
    if (message.folder && message.folder.title === 'Inbox') {
        mixpanelObject['Inbox Messages'] = 1;
    } else if (message.folder) {
        mixpanelObject['Folder Messages'] = 1;
    }

    if (message.title) {
        mixpanelObject['Title Messages'] = 1;
    }
    if (message.isCheckbox) {
        mixpanelObject['Task Messages'] = 1;
    }
    if (message.isStarred) {
        mixpanelObject['Star Messages'] = 1;
    }
    if (message.body) {
        mixpanelObject['Note Messages'] = 1;
    }
    if (message.image) {
        mixpanelObject['Image Messages'] = 1;
    }
    if (message.file) {
        mixpanelObject['File Messages'] = 1;

        if (message.file.type && message.file.type.includes('audio')) {
            mixpanelObject['Audio Messages'] = 1;
        }
    }
    if (urlMatches && urlMatches.length > 0) {
        mixpanelObject['Link Messages'] = 1;
    }
    mixpanelObject['Total Messages'] = 1;

    return mixpanelObject;
}

function createMessageAnalyticsObj(message, messageType, justCreated) {
    // console.log('message', message);
    const userId = message.folder?.owner;

    const mixpanelObject = extractMixpanelObject(message);
    const messageContent = extractMessageInfo(message, justCreated);

    messageContent["msg_type"] = messageType;

    return { messageContent, mixpanelObject, userId };
}

function message_sent(message, actionObject) {
    let { messageContent, mixpanelObject, userId } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB, true);

    if (userId) {
        safeIdentify(userId);
    }

    safeTrack('Message Sent', {
        ...messageContent,
        ...actionObject
    });

    safePeopleIncrement(mixpanelObject);

    safeReactGA('event', {
        category: 'Message',
        action: 'Sent Message'
    });
}

function message_updated(message) {
    let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);

    safeTrack('Message Updated', messageContent);

    safeReactGA('event', {
        category: 'Message',
        action: 'Updated Message'
    });
}

function ui_action(actionObject) {
    // console.log('action', action);
    // ReactGA.event({
    //     category: 'UI Action',
    //     action: action
    // });
    safeTrack(`UI Action`, actionObject);
}

// Add new function for tracking feature votes
function feature_vote(feature, source, user) {
    const featureKey = feature.replace(/\s+/g, '_');
    
    // Track the vote
    safeTrack('Feature Vote', {
        feature,
        source,
        user_id: user?.id,
        timestamp: new Date().toISOString()
    });

    // Set the vote in user properties
    safePeopleSet({
        [`voted_${featureKey}`]: true,
        last_vote_date: new Date().toISOString()
    });

    // Increment feature-specific counter
    safePeopleIncrement({
        [`${featureKey}_votes`]: 1,
        'total_feature_votes': 1
    });

    safeReactGA('event', {
        category: 'Feature Votes',
        action: `Vote for ${feature}`,
        label: source
    });

    // Store vote in local storage as backup
    try {
        localStorage.setItem(`voted_${featureKey}`, 'true');
    } catch (error) {
        console.error('Error storing vote in localStorage:', error);
    }
}

// Helper function to check if user has voted for a feature
async function has_voted_for_feature(feature) {
    const featureKey = feature.replace(/\s+/g, '_');
    try {
        // Check Mixpanel first
        const distinctId = await safeGetDistinctId();
        const hasVoted = await safeGetProperty(`voted_${featureKey}`);
        
        // If not found in Mixpanel, check localStorage as backup
        if (!hasVoted) {
            const localVote = localStorage.getItem(`voted_${featureKey}`);
            if (localVote === 'true') {
                // Sync back to Mixpanel if found in localStorage
                safePeopleSet({
                    [`voted_${featureKey}`]: true
                });
                return true;
            }
        }
        
        return !!hasVoted;
    } catch (error) {
        console.error('Error checking vote status:', error);
        // Fallback to localStorage if Mixpanel fails
        return localStorage.getItem(`voted_${featureKey}`) === 'true';
    }
}

function create_subfolder_from_folder() {
    safeTrack('Folder Created', {
        "type": "Subfolder",
        "from": "HomeFolder"
    });
    safePeopleIncrement("Total Folders", 1)
    safeReactGA('event', {
        category: 'Product',
        action: 'Create Subfolder from HomeFolder'
    });
}


function show_deleted_messages() {
    safeTrack('Show Trashed Messages');
    safeReactGA('event', {
        category: 'Messages',
        action: 'Show Trashed Messages Click'
    });
}

function search_active(obj) {
    safeTrack('Search Active', obj);
    safeReactGA('event', {
        category: 'Search',
        action: 'Search View Open'
    });
}

function search_action(obj) {
    safeTrack('Search Action', obj);
}

function subfolder_open() {
    safeTrack('Folder View', {
        "type": "Subfolder"
    });
    safeReactGA('event', {
        category: 'Subfolder',
        action: 'Subfolder View Open'
    });
}

function filter_actived(activeFilters) {
    let reee = activeFilters.reduce((a, v) => ({ ...a, [v]: v }), {});
    if (activeFilters && activeFilters.length > 0) {
        safeTrack('Folder Filters Active', {
            "filters": activeFilters
        });
        safeReactGA('event', {
            category: 'Filters',
            action: 'Filter Active Click',
            value: activeFilters.length
        });
    } else {
        safeTrack('Folder Filters Cleared');

        safeReactGA('event', {
            category: 'Filters',
            action: 'Filter Cleared Click'
        });
    }
}

function search_filter_actived(activeFilters) {
    let reee = activeFilters.reduce((a, v) => ({ ...a, [v]: v }), {});
    if (activeFilters && activeFilters.length > 0) {
        safeTrack('Search Filters Active', {
            "filters": activeFilters
        });
        safeReactGA('event', {
            category: 'Search Filters',
            action: 'Filter Active Click',
            value: activeFilters.length
        });
    } else {
        safeTrack('Search Filters Cleared');
        safeReactGA('event', {
            category: 'Search Filters',
            action: 'Filter Cleared Click'
        });
    }
}

function mobile_download(mobilePlatform) {
    safeReactGA('event', mobilePlatform);
    if (mobilePlatform === SAVED_TYPE.IOS) {
        safeTrack('App Download', {
            "download_device": 'Download iOS'
        });
        safeReactGA('event', {
            category: 'User',
            action: 'Download iOS'
        });
    } else {
        safeTrack('App Download', {
            "download_device": 'Download Android'
        });
        safeReactGA('event', {
            category: 'User',
            action: 'Download Android'
        });
    }
}

function trash_messages(messages) {
    let count = messages.length
    messages.forEach(message => {
        let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);
        safeTrack('Message Action', {
            "action": "Trash",
            "multi_select": count,
            ...messageContent
        });
    });

    safePeopleIncrement({
        "Total Messages": -count,
        "Total Trash Messages": count
    })

    safeReactGA('event', {
        category: 'Message',
        action: 'Trash Messages',
        value: count
    });
}

function restore_messages(messages) {
    let count = messages.length
    messages.forEach(message => {
        let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);
        safeTrack('Message Action', {
            "action": "Restore",
            "multi_select": count,
            ...messageContent
        });
    });

    safePeopleIncrement({
        "Total Messages": count,
        "Total Trash Messages": -count
    })

    safeReactGA('event', {
        category: 'Message',
        action: 'Restore',
        value: count
    });
}

function delete_messages(messages) {
    let count = messages.length
    messages.forEach(message => {
        let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);
        safeTrack('Message Action', {
            "action": "Delete",
            "multi_select": count,
            ...messageContent
        });
    });

    safePeopleIncrement({
        "Total Trash Messages": -count,
        "Total Deleted Messages": count
    })

    safeReactGA('event', {
        category: 'Message',
        action: 'Delete',
        value: count
    });
}

function trash_folder(count) {
    safeTrack('Folder Action', {
        "action": "Trash"
    });
    safePeopleIncrement({
        "Total Folders": -1,
        "Total Trash Folders": 1
    })
    safeReactGA('event', {
        category: 'Folder',
        action: 'Trash'
    });
}

function restore_folder(count) {
    safeTrack('Folder Action', {
        "action": "Restore"
    });
    safePeopleIncrement({
        "Total Folders": 1,
        "Total Trash Folders": -1
    })
    safeReactGA('event', {
        category: 'Folder',
        action: 'Restore'
    });
}

async function delete_folder(count) {
    safeTrack('Folder Action', {
        "action": "Delete"
    });
    safePeopleIncrement({
        "Total Trash Folders": -1,
        "Total Deleted Folders": 1
    })
    safeReactGA('event', {
        category: 'Folder',
        action: 'Delete'
    });
}

// not active on desktop yet START
// async function share_message_click() {
//     ReactGA.event({
//         category: 'Message View',
//         action: 'Share Message Click'
//     });
//     mixpanel.track('Message Action', {
//         "action": "Share"
//     });
// }

// async function copy_message_body_click() {
//     ReactGA.event({
//         category: 'Message View',
//         action: 'Copy Message Body Click'
//     });
//     mixpanel.track('Copy Message Body Click');
// }
// not active on desktop yet END

async function message_action(actionObject, message) {
    let mesageInfo = {}
    if (message) {
        let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB);
        mesageInfo = messageContent;
    }

    safeTrack('Message Action', {
        ...actionObject,
        ...mesageInfo
    });
}

async function message_edit(actionObject, message, justCreated) {
    let mesageInfo = {}
    if (message) {
        let { messageContent } = createMessageAnalyticsObj(message, SAVED_TYPE.WEB, justCreated);
        mesageInfo = messageContent;
    }
    safeTrack('Message Edit', {
        ...actionObject,
        ...mesageInfo
    });
}

async function dash_task() {
    safeTrack('Message Edit', {
        'action': "Task",
        "quick": true
    });
    safePeopleSet('Task Messages', 1)
}

async function help_center_open() {
    safeTrack('Help Center Open');
    safeReactGA('event', {
        category: 'Product',
        action: 'Help Center Open'
    });
}


async function task_from_tasks_folder() {
    safeTrack('Task from Tasks Folder');
}

function create_folder_from_move_message() {
    safeTrack('Folder Created', {
        'type': "HomeFolder",
        'from': 'Move Message'
    });
    safePeopleIncrement("Total Folders", 1)
    safeReactGA('event', {
        category: 'Product',
        action: 'Create HomeFolder - From Move Message'
    });
}

function testimonial_open() {
    safeReactGA('event', {
        category: 'Homepage',
        action: 'Testimonial Open'
    });
}

function landingpage_action(actionObject) {
    safeTrack('Landing Page', actionObject);
}

function create_home_folder(fromLocation) {
    safeTrack('Folder Created', {
        'type': "HomeFolder",
        'from': fromLocation ? fromLocation : 'Default'
    });
    safePeopleIncrement("Total Folders", 1)
    safeReactGA('event', {
        category: 'Product',
        action: 'Create HomeFolder - From Folders list'
    });
}

// not used
async function folder_action(actionObject) {
    safeTrack('Folder Action', actionObject);
}

async function folder_edit(actionObject, folder) {
    let folderInfo = {}
    if (folder) {
        folderInfo = extractFolderInfo(folder)
    }
    safeTrack('Folder Edit', {
        ...actionObject,
        ...folderInfo
    });
}

async function user_action(actionObject) {
    safeTrack('User Action', actionObject);
}

async function user_edit(actionObject) {
    safeTrack('User Edit', actionObject);
}

function discovery_source_submitted(discoverySource, discoverySourceOther) {
    safeTrack('Discovery Source Submitted', {
        'discovery_source': discoverySource,
        'discovery_source_other': discoverySourceOther || null,
        'timestamp': new Date().toISOString()
    });
    
    // Set as user property
    const peopleData = {
        'Discovery Source': discoverySource
    };
    
    if (discoverySourceOther) {
        peopleData['Discovery Source Other'] = discoverySourceOther;
    }
    
    safePeopleSet(peopleData);
    
    safeReactGA('event', {
        category: 'User',
        action: 'Discovery Source Submitted',
        label: discoverySource
    });
}

function app_rating_feedback(improvementReasons, improvementFeedbackOther, feedbackType) {
    try {
        const feedbackData = {
            feedback_type: feedbackType || 'not_bad', // 'not_bad', 'good_job', 'not_now'
            improvement_reasons: improvementReasons || [],
            improvement_reasons_count: improvementReasons?.length || 0,
            has_additional_feedback: !!improvementFeedbackOther,
            additional_feedback_length: improvementFeedbackOther?.length || 0
        };

        // Track individual reasons
        if (improvementReasons && improvementReasons.length > 0) {
            improvementReasons.forEach(reason => {
                feedbackData[`reason_${reason}`] = true;
            });
        }

        // Include feedback if provided (truncated for privacy/analytics limits)
        if (improvementFeedbackOther) {
            feedbackData.additional_feedback_preview = improvementFeedbackOther.substring(0, 200);
        }

        safeTrack('App Rating Feedback', feedbackData);
        
        safeReactGA('event', {
            category: 'App Rating',
            action: 'Feedback Submitted',
            label: feedbackType
        });
    } catch (error) {
        // Don't throw - analytics failures shouldn't block user flow
        if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to track app rating feedback in Mixpanel:', error);
        }
        // Silently fail - user flow should proceed even if analytics fails
    }
}

function trial_expired_feedback(reason, metadata = {}) {
    safeTrack('Trial Expired Feedback', {
        feedback_reason: reason || 'unknown',
        has_feedback_reason: Boolean(reason),
        source: metadata.source || 'trial_expired_email',
        path: typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : null,
        timestamp: new Date().toISOString()
    });
}

function messaging_integration(action, channel) {
    if (!['WHATSAPP', 'TELEGRAM'].includes(channel)) return;
    const eventNames = {
        connect_clicked: 'Integration Connect Clicked',
        chat_opened: 'Integration Chat Opened',
        connect_failed: 'Integration Connect Failed',
        disconnect_clicked: 'Integration Disconnect Clicked'
    };
    if (eventNames[action]) safeTrack(eventNames[action], { channel, integration: channel.toLowerCase() });
}

export default {
    messaging_integration,
    pageview,
    app_pageview,
    reminder_action,
    ai_suggestion,
    checkout_started,
    purchase_completed,
    published_folder_viewed,
    published_folder_action,
    published_folder_signup_conversion,
    messageview,
    addAnalytics,
    addProfilePic,
    addBackgroundPic,
    removeProfilePic,
    landingpage_action,
    search_filter_actived,
    help_center_open,
    dash_task,
    message_action,
    message_edit,
    task_from_tasks_folder,
    event,
    modalview,
    sign_up,
    sign_up_pro,
    login,
    sign_out,
    delete_account,
    message_sent,
    message_updated,
    ui_action,
    create_home_folder,
    create_subfolder_from_folder,
    show_deleted_messages,
    search_active,
    search_action,
    subfolder_open,
    filter_actived,
    mobile_download,
    trash_messages,
    delete_messages,
    trash_folder,
    delete_folder,
    create_folder_from_move_message,
    testimonial_open,
    folder_action,
    folder_edit,
    restore_messages,
    restore_folder,
    user_action,
    user_edit,
    feature_vote,
    has_voted_for_feature,
    discovery_source_submitted,
    app_rating_feedback,
    trial_expired_feedback
};
